import React, { useEffect, useRef, useState } from 'react';
const ReconstructionViewer = React.lazy(() => import('./ReconstructionViewer'));
import axios from 'axios';
import ElementContainer from 'components/shared/ElementContainer';

const PROGRESS_STEPS = [
  { label: 'Preparing source', completeAt: 30, icon: 'bx-file' },
  { label: 'Loading 3D model', completeAt: 50, icon: 'bx-chip' },
  { label: 'Reconstructing scene', completeAt: 86, icon: 'bx-cube' },
  { label: 'Building preview', completeAt: 98, icon: 'bx-shape-square' },
  { label: 'Saving reconstruction', completeAt: 100, icon: 'bx-cloud-upload' },
];

const ReconstructionArtifact = (props) => {
  const {
    id,
    user,
    token,
    title,
    titleColor,
    prompt,
    requirement,
    requirable,
    reviewable,
    maxImages,
    maxUploadSizeBytes,
    maxUploadSizeMb,
  } = props;

  const initialMedia = props.media || null;
  const initialReconstruction = props.reconstruction || null;

  const [media, setMedia] = useState(initialMedia);
  const [reconstruction, setReconstruction] = useState(initialReconstruction);
  const [status, setStatus] = useState(props.status);
  const [completed, setCompleted] = useState(props.completed);
  const [sourceType, setSourceType] = useState('video');
  const [upload, setUpload] = useState(null);
  const [images, setImages] = useState([]);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showReplaceWarning, setShowReplaceWarning] = useState(false);
  const imageFilesInputRef = useRef(null);
  const imageFolderInputRef = useRef(null);

  const endpoint = axios.create({
    headers: { authorization: `bearer ${token}` },
  });

  useEffect(() => {
    if (!media || !reconstruction) return undefined;
    if (reconstruction.status === 'completed' || reconstruction.status === 'failed') {
      return undefined;
    }

    const interval = setInterval(() => {
      endpoint
        .get(`/client_api/v1/mixed_medias/${media.id}`)
        .then((response) => {
          setReconstruction(response.data.reconstruction);
          if (response.data.status !== undefined) setStatus(response.data.status);
          if (response.data.completed !== undefined) setCompleted(response.data.completed);
        })
        .catch(() => {
          // A transient polling failure should not discard the student's job.
        });
    }, 5000);

    return () => clearInterval(interval);
  }, [media && media.id, reconstruction && reconstruction.status]);

  const resetReconstruction = () => {
    setMedia(null);
    setReconstruction(null);
    setUpload(null);
    setImages([]);
    setError(null);
    setShowReplaceWarning(false);
  };

  const requestAnotherReconstruction = () => {
    setShowReplaceWarning(true);
  };

  const changeSourceType = (type) => {
    setSourceType(type);
    setUpload(null);
    setImages([]);
    setError(null);
  };

  const selectedBytes = () => {
    if (sourceType === 'images') {
      return images.reduce((total, image) => total + image.size, 0);
    }
    return upload ? upload.size : 0;
  };

  const validateSource = () => {
    if (sourceType === 'images') {
      if (images.length < 1) return 'Please select at least 1 image.';
      if (maxImages && images.length > maxImages) {
        return `Please select no more than ${maxImages} images.`;
      }
      const invalid = images.some((image) => !['jpg', 'jpeg', 'png'].includes(image.name.split('.').pop().toLowerCase()));
      if (invalid) return 'Images must be JPG, JPEG, or PNG files.';
      if (maxUploadSizeBytes && selectedBytes() > maxUploadSizeBytes) {
        return `The image set must be ${maxUploadSizeMb || 300} MB or smaller in total.`;
      }
      return null;
    }

    if (!upload) {
      return sourceType === 'glb' ? 'Please select a GLB file.' : 'Please select a drone video first.';
    }

    const extension = upload.name.split('.').pop().toLowerCase();
    if (sourceType === 'video' && !['mp4', 'mov', 'avi'].includes(extension)) {
      return 'Please upload an MP4, MOV, or AVI video.';
    }
    if (sourceType === 'glb' && extension !== 'glb') {
      return 'Please upload a GLB file.';
    }
    if (maxUploadSizeBytes && upload.size > maxUploadSizeBytes) {
      return `${sourceType === 'glb' ? 'GLB file' : 'Video'} must be ${maxUploadSizeMb || 300} MB or smaller.`;
    }
    return null;
  };

  const submitSource = (event) => {
    event.preventDefault();

    const validationError = validateSource();
    if (validationError) {
      setError(validationError);
      return;
    }

    setUploading(true);
    setError(null);

    const data = new FormData();
    data.set('source_type', sourceType);
    data.set('requirement_id', requirement.id);

    if (sourceType === 'images') {
      images.forEach((image) => data.append('mixed_media[images][]', image));
      data.set('mixed_media[description]', 'Image-set reconstruction source');
    } else {
      data.set('mixed_media[upload]', upload);
      data.set(
        'mixed_media[description]',
        sourceType === 'glb' ? 'Uploaded 3D model' : 'Drone reconstruction source video'
      );
    }

    endpoint
      .post('/client_api/v1/mixed_medias', data, {
        headers: { 'content-type': 'multipart/form-data' },
      })
      .then((response) => {
        setMedia(response.data.media);
        setReconstruction(response.data.reconstruction);
        if (response.data.status !== undefined) setStatus(response.data.status);
        if (response.data.completed !== undefined) setCompleted(response.data.completed);
        setUploading(false);
      })
      .catch((requestError) => {
        const message =
          requestError.response?.data?.error || 'The reconstruction source could not be uploaded.';
        setError(message);
        setUploading(false);
      });
  };

  const progress = reconstruction?.progress || 0;
  const active = reconstruction && ['queued', 'processing'].includes(reconstruction.status);

  const statusLabel = () => {
    if (!reconstruction) return null;
    if (reconstruction.status === 'completed') return 'Complete';
    if (reconstruction.status === 'failed') return 'Needs attention';
    if (reconstruction.status === 'queued') return 'Queued';
    return 'Processing';
  };

  const currentStepIndex = () => {
    if (reconstruction?.status === 'queued') return 0;
    const index = PROGRESS_STEPS.findIndex((step) => progress < step.completeAt);
    return index === -1 ? PROGRESS_STEPS.length - 1 : index;
  };

  const sourceSummary = () => {
    if (!reconstruction) return null;
    if (reconstruction.source_type === 'images') {
      const count = reconstruction.source_image_count || 0;
      return `${count} source image${count === 1 ? '' : 's'}`;
    }
    if (reconstruction.source_type === 'glb') {
      return media?.upload_filename || 'Uploaded GLB';
    }
    return media?.upload_filename;
  };

  const styles = {
    shell: {
      border: '1px solid #e1e5ea',
      borderRadius: '12px',
      background: '#fff',
      overflow: 'hidden',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '12px',
      padding: '16px 18px',
      borderBottom: '1px solid #eceff2',
      background: '#f8f9fb',
    },
    headerTitle: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      margin: 0,
      fontSize: '17px',
      fontWeight: 700,
    },
    statusPill: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      borderRadius: '999px',
      padding: '5px 10px',
      background: reconstruction?.status === 'completed' ? '#e9f8ee' : '#edf4ff',
      color: reconstruction?.status === 'completed' ? '#237a3b' : '#315f9b',
      fontSize: '12px',
      fontWeight: 700,
      whiteSpace: 'nowrap',
    },
    body: { padding: '18px' },
    uploadPanel: {
      border: '1px dashed #bcc7d3',
      borderRadius: '10px',
      background: '#fafbfc',
      padding: '20px',
    },
    sourceOptions: {
      display: 'flex',
      gap: '8px',
      flexWrap: 'wrap',
      marginBottom: '16px',
    },
    sourceOption: {
      flex: '1 1 150px',
      border: '1px solid #d7dde4',
      borderRadius: '8px',
      padding: '10px 12px',
      background: '#fff',
      cursor: 'pointer',
    },
    source: {
      display: 'flex',
      alignItems: 'center',
      gap: '7px',
      margin: '0 0 16px',
      color: '#69727d',
      fontSize: '13px',
    },
    progressHeading: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '16px',
      marginBottom: '6px',
    },
    progressPercent: {
      flex: '0 0 auto',
      marginLeft: '12px',
      color: '#59636f',
      fontWeight: 600,
    },
    stepList: { margin: '14px 0 8px', padding: 0, listStyle: 'none' },
    step: {
      display: 'flex',
      alignItems: 'center',
      minHeight: '42px',
      gap: '11px',
      transition: 'opacity 250ms ease, transform 250ms ease',
    },
    check: {
      width: '26px',
      height: '26px',
      borderRadius: '50%',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      flex: '0 0 26px',
      transition: 'all 250ms ease',
    },
    actions: { display: 'flex', gap: '12px', flexWrap: 'wrap' },
    action: { flex: '1 1 220px', margin: 0 },
    imagePickerActions: {
      display: 'flex',
      justifyContent: 'center',
      gap: '10px',
      flexWrap: 'wrap',
      marginTop: '14px',
    },
    hiddenFileInput: {
      display: 'none',
    },

  };

  return (
    <ElementContainer
      id={id}
      user={user}
      token={token}
      color={titleColor}
      title={title}
      requirable={requirable}
      reviewable={reviewable}
      status={status}
      completed={completed || reconstruction?.status === 'completed'}
      icon={'bx-cube-alt'}
    >
      <div className="m-bottom-1">{prompt}</div>

      {error && <div className="danger radius pad-1 m-bottom-1">{error}</div>}

      {!reconstruction && (
        <div style={styles.shell}>
          <div style={styles.header}>
            <h3 style={styles.headerTitle}>
              <i className="bx bx-cube-alt" aria-hidden="true" />
              3D Reconstruction
            </h3>
            <span style={{ ...styles.statusPill, background: '#f0f2f5', color: '#59636f' }}>
              Ready
            </span>
          </div>
          <div style={styles.body}>
            <form onSubmit={submitSource}>
              <div style={styles.sourceOptions}>
                {[
                  ['video', 'Video', 'bx-video'],
                  ['images', 'Images', 'bx-images'],
                  ['glb', 'Existing GLB', 'bx-cube'],
                ].map(([type, label, icon]) => (
                  <label
                    key={type}
                    style={{
                      ...styles.sourceOption,
                      borderColor: sourceType === type ? '#5d84b3' : '#d7dde4',
                      background: sourceType === type ? '#f1f6fc' : '#fff',
                    }}
                  >
                    <input
                      type="radio"
                      name="reconstruction-source"
                      value={type}
                      checked={sourceType === type}
                      onChange={() => changeSourceType(type)}
                    />{' '}
                    <i className={`bx ${icon}`} aria-hidden="true" /> {label}
                  </label>
                ))}
              </div>

              <div style={styles.uploadPanel}>
                {sourceType === 'video' && (
                  <>
                    <div className="text-center m-bottom-1">
                      <i className="bx bx-cloud-upload" style={{ fontSize: '34px', color: '#6d7f91' }} aria-hidden="true" />
                      <div className="text-bold">Choose a drone video</div>
                      <div className="small-text inactive-text">
                        MP4, MOV, or AVI. Maximum {maxUploadSizeMb || 300} MB.
                      </div>
                    </div>
                    <input
                      type="file"
                      accept="video/mp4,video/quicktime,video/x-msvideo,.mp4,.mov,.avi"
                      onChange={(event) => setUpload(event.target.files[0] || null)}
                    />
                  </>
                )}

                {sourceType === 'images' && (
                  <>
                    <div className="text-center m-bottom-1">
                      <i className="bx bx-images" style={{ fontSize: '34px', color: '#6d7f91' }} aria-hidden="true" />
                      <div className="text-bold">Choose reconstruction images</div>
                      <div className="small-text inactive-text">
                        JPG or PNG. Select 1–{maxImages || 300} images, up to {maxUploadSizeMb || 300} MB total.
                      </div>
                    </div>
                    <div style={styles.imagePickerActions}>
                      <button
                        className="btn btn-secondary radius"
                        type="button"
                        onClick={() => imageFilesInputRef.current?.click()}
                      >
                        <i className="bx bx-images" aria-hidden="true" /> Choose Images
                      </button>
                      <button
                        className="btn btn-secondary radius"
                        type="button"
                        onClick={() => imageFolderInputRef.current?.click()}
                      >
                        <i className="bx bx-folder-open" aria-hidden="true" /> Choose Folder
                      </button>
                    </div>
                    <input
                      ref={imageFilesInputRef}
                      style={styles.hiddenFileInput}
                      type="file"
                      accept="image/jpeg,image/png,.jpg,.jpeg,.png"
                      multiple
                      onChange={(event) => setImages(Array.from(event.target.files || []))}
                    />
                    <input
                      ref={imageFolderInputRef}
                      style={styles.hiddenFileInput}
                      type="file"
                      accept="image/jpeg,image/png,.jpg,.jpeg,.png"
                      multiple
                      webkitdirectory=""
                      directory=""
                      onChange={(event) => setImages(Array.from(event.target.files || []))}
                    />
                    {images.length > 0 && (
                      <div className="small-text text-center m-top-1">
                        <i className="bx bx-check-circle" aria-hidden="true" /> {images.length} images selected
                      </div>
                    )}
                  </>
                )}

                {sourceType === 'glb' && (
                  <>
                    <div className="text-center m-bottom-1">
                      <i className="bx bx-cube" style={{ fontSize: '34px', color: '#6d7f91' }} aria-hidden="true" />
                      <div className="text-bold">Upload an existing 3D model</div>
                      <div className="small-text inactive-text">
                        GLB only. Maximum {maxUploadSizeMb || 300} MB. No GPU reconstruction is required.
                      </div>
                    </div>
                    <input
                      type="file"
                      accept="model/gltf-binary,.glb"
                      onChange={(event) => setUpload(event.target.files[0] || null)}
                    />
                  </>
                )}

                {upload && sourceType !== 'images' && (
                  <div className="small-text m-top-1">
                    <i className={`bx ${sourceType === 'glb' ? 'bx-cube' : 'bx-video'}`} aria-hidden="true" /> {upload.name}
                  </div>
                )}
              </div>

              <button className="btn radius expand m-top-1" disabled={uploading} type="submit">
                <i className={`bx ${uploading ? 'bx-loader-alt bx-spin' : sourceType === 'glb' ? 'bx-upload' : 'bx-cube-alt'}`} aria-hidden="true" />{' '}
                {uploading
                  ? 'Uploading...'
                  : sourceType === 'glb'
                    ? 'Use 3D Model'
                    : 'Create 3D Reconstruction'}
              </button>
            </form>
          </div>
        </div>
      )}

      {reconstruction && (
        <div style={styles.shell}>
          <div style={styles.header}>
            <h3 style={styles.headerTitle}>
              <i className="bx bx-cube-alt" aria-hidden="true" />
              3D Reconstruction
            </h3>
            <span
              style={
                reconstruction.status === 'failed'
                  ? { ...styles.statusPill, background: '#fff0f0', color: '#b63333' }
                  : styles.statusPill
              }
            >
              <i
                className={`bx ${
                  reconstruction.status === 'completed'
                    ? 'bx-check-circle'
                    : reconstruction.status === 'failed'
                      ? 'bx-error-circle'
                      : 'bx-loader-alt bx-spin'
                }`}
                aria-hidden="true"
              />
              {statusLabel()}
            </span>
          </div>

          <div style={styles.body}>
            {sourceSummary() && (
              <p style={styles.source}>
                <i className={`bx ${reconstruction.source_type === 'images' ? 'bx-images' : reconstruction.source_type === 'glb' ? 'bx-cube' : 'bx-video'}`} aria-hidden="true" />
                <span>{sourceSummary()}</span>
              </p>
            )}

            {active && (
              <div>
                <div className="small-text" style={styles.progressHeading}>
                  <strong>{reconstruction.status === 'queued' ? 'Waiting for a GPU worker' : 'Building your 3D reconstruction'}</strong>
                  <span style={styles.progressPercent}>{progress}%</span>
                </div>
                <progress value={progress} max="100" className="expand" />

                <ul style={styles.stepList} aria-label="Reconstruction progress">
                  {PROGRESS_STEPS.map((step, index) => {
                    const complete = progress >= step.completeAt;
                    const current = index === currentStepIndex() && !complete;
                    return (
                      <li
                        key={step.label}
                        style={{
                          ...styles.step,
                          opacity: complete || current ? 1 : 0.48,
                          transform: complete || current ? 'translateX(0)' : 'translateX(3px)',
                        }}
                      >
                        <span
                          style={{
                            ...styles.check,
                            border: complete ? '1px solid #4a9b61' : current ? '1px solid #6b91bf' : '1px solid #cbd2d9',
                            background: complete ? '#e9f8ee' : current ? '#edf4ff' : '#fff',
                            color: complete ? '#2f8147' : current ? '#426d9f' : '#8a949e',
                          }}
                        >
                          <i className={`bx ${complete ? 'bx-check' : current ? 'bx-loader-alt bx-spin' : step.icon}`} aria-hidden="true" />
                        </span>
                        <div>
                          <div style={{ fontWeight: complete || current ? 600 : 400 }}>{step.label}</div>
                          {current && (
                            <div className="small-text inactive-text">
                              {reconstruction.status === 'queued' ? 'Waiting to start...' : 'In progress...'}
                            </div>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {reconstruction.status === 'completed' && reconstruction.output_url && (
              <div>
                <div className="success radius pad-1 m-bottom-1">
                  <i className="bx bx-check-circle" aria-hidden="true" />{' '}
                  Your 3D model is ready.
                </div>

                <React.Suspense fallback={<div className="pad-2 text-center">Loading 3D viewer…</div>}>
                  <ReconstructionViewer
                    key={reconstruction.id || media.id}
                    url={reconstruction.output_url}
                    onCreateAnother={requestAnotherReconstruction}
                    storageKey={`cs2n-reconstruction-measurements-v1:${user?.id || 'user'}:${media.id}:${reconstruction.id || 'model'}`}
                  />
                </React.Suspense>

                {showReplaceWarning && (
                  <div className="warning radius pad-1 m-top-1">
                    <div className="text-bold m-bottom-half">
                      <i className="bx bx-error" aria-hidden="true" /> Creating another will replace this 3D model
                    </div>
                    <div className="small-text m-bottom-1">
                      Download the GLB first if you want to keep a copy of this reconstruction.
                    </div>
                    <div style={styles.actions}>
                      <button
                        className="btn btn-secondary radius"
                        style={styles.action}
                        type="button"
                        onClick={() => setShowReplaceWarning(false)}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn radius"
                        style={styles.action}
                        type="button"
                        onClick={resetReconstruction}
                      >
                        Continue and Replace
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {reconstruction.status === 'completed' && !reconstruction.output_url && (
              <div>
                <div className="success radius pad-1 m-bottom-1">
                  <i className="bx bx-check-circle" aria-hidden="true" /> Reconstruction completed successfully.
                </div>
                <button className="btn btn-secondary radius expand" type="button" onClick={resetReconstruction}>
                  <i className="bx bx-refresh" aria-hidden="true" /> Create Another Reconstruction
                </button>
              </div>
            )}

            {reconstruction.status === 'failed' && (
              <div>
                <div className="danger radius pad-1 m-bottom-1">
                  <div className="text-bold m-bottom-half">
                    <i className="bx bx-error-circle" aria-hidden="true" /> Reconstruction couldn't be completed
                  </div>
                  <div className="small-text">
                    {reconstruction.error_message || 'The reconstruction could not be completed.'}
                  </div>
                </div>
                <button className="btn btn-secondary radius expand" type="button" onClick={resetReconstruction}>
                  <i className="bx bx-refresh" aria-hidden="true" /> Try Another Source
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </ElementContainer>
  );
};

export default ReconstructionArtifact;
